import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import DocumentService from "services/DocumentService"

const initialState = {
  documents: [],
  loading: false,
  error: null,
  totalItems: null,
  currentPage: 1,
}

export const getDocuments = createAsyncThunk(
  "documents/getAll",
  async (
    { page = 1, searchValue = "", clientId = null },
    { rejectWithValue, dispatch }
  ) => {
    try {
      dispatch(setCurrentPage(page))
      const response = clientId
        ? await DocumentService.getDocumentsByClient(clientId)
        : await DocumentService.getAllDocuments({
            query: searchValue,
            page,
          })
      dispatch(clearStates())
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Error")
    }
  }
)

export const getDocumentById = createAsyncThunk(
  "documents/getById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await DocumentService.getDocumentById(id)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Error")
    }
  }
)

export const createDocument = createAsyncThunk(
  "documents/create",
  async ({ clientId, data }, { rejectWithValue }) => {
    try {
      const response = await DocumentService.createDocument(clientId, data)
      return response
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Error")
    }
  }
)

export const updateDocument = createAsyncThunk(
  "documents/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await DocumentService.updateDocumentById(id, data)
      return response
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Error")
    }
  }
)

export const deleteDocument = createAsyncThunk(
  "documents/delete",
  async (id, { rejectWithValue }) => {
    try {
      const response = await DocumentService.deleteDocumentById(id)
      return response
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Error")
    }
  }
)

const documentSlice = createSlice({
  name: "documents",
  initialState,
  reducers: {
    showLoading: (state) => {
      state.loading = true
    },
    hideLoading: (state) => {
      state.loading = false
    },
    hideError: (state) => {
      state.error = null
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload
    },
    clearStates: (state) => {
      state.documents = []
      state.totalItems = null
    },
  },
  extraReducers: (builder) => {
    builder
      // GET ALL
      .addCase(getDocuments.pending, (state) => {
        state.loading = true
        state.error = null
      })
    .addCase(getDocuments.fulfilled, (state, action) => {
        const payloadData = action.payload?.data
        const isArrayPayload =
          Array.isArray(action.payload) && action.payload.length > 0
        const documentsArray = Array.isArray(payloadData)
          ? payloadData
          : isArrayPayload
          ? action.payload
          : []
        state.documents = documentsArray
        state.totalItems =
          action.payload?.totalItems ??
          action.payload?.total ??
          documentsArray.length
        state.loading = false
      })
      .addCase(getDocuments.rejected, (state, action) => {
        state.documents = []
        state.loading = false
        state.error = action.payload
      })

      // GET BY ID
      .addCase(getDocumentById.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getDocumentById.fulfilled, (state) => {
        state.loading = false
      })
      .addCase(getDocumentById.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // CREATE
      .addCase(createDocument.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createDocument.fulfilled, (state) => {
        state.loading = false
      })
      .addCase(createDocument.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // UPDATE
      .addCase(updateDocument.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateDocument.fulfilled, (state) => {
        state.loading = false
      })
      .addCase(updateDocument.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // DELETE
      .addCase(deleteDocument.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteDocument.fulfilled, (state) => {
        state.loading = false
      })
      .addCase(deleteDocument.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  },
})

export default documentSlice.reducer

export const {
  showLoading,
  hideLoading,
  hideError,
  setCurrentPage,
  clearStates,
} = documentSlice.actions
